'use client'

import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { Button } from './ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card'
import { Switch } from './ui/switch'
import { Label } from './ui/label'
import Link from 'next/link'

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false)
  const [statisticsConsent, setStatisticsConsent] = useState(true)

  // Dynamically load Google Tag Manager after consent
  const loadGTM = () => {
    const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
    if (!gtmId) return;

    // Avoid inserting the script multiple times
    const existing = document.querySelector(
      `script[src*="https://www.googletagmanager.com/gtm.js?id=${gtmId}"]`
    );
    if (existing) return;

    window.dataLayer = window.dataLayer || [];
    // Define gtag helper so we can use Consent Mode updates via GTM
    window.gtag = (...args: any[]) => {
      (window.dataLayer as any[]).push(args as any);
    };
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    script.async = true;
    document.head.appendChild(script);
  };

  useEffect(() => {
    // Check stored consent and auto-load GTM if statistics consent was previously granted
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
      return;
    }
    try {
      const parsed = JSON.parse(consent);
      setShowBanner(false);
      if (parsed?.statistics) {
        loadGTM();
      }
    } catch {
      // If parsing fails, show banner again
      setShowBanner(true);
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({ necessary: true, statistics: statisticsConsent }));
    setShowBanner(false);

    if (statisticsConsent) {
      // Load GTM container after consent
      loadGTM();
      // Update Google Consent Mode for analytics (handled via GTM)
      try {
        window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
      } catch {}
    }
  }

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({ necessary: true, statistics: false }));
    setShowBanner(false);
    // If GTM was previously loaded in this session, explicitly deny analytics storage
    try {
      window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
    } catch {}
  }

  if (!showBanner) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur-sm z-50">
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-2xl font-bold">Cookie Settings</CardTitle>
          <Button variant="ghost" size="icon" onClick={() => setShowBanner(false)}>
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            We use cookies to provide you with the best possible experience on our website. 
            Some are necessary for the operation of the site, while others help us improve 
            the site and provide you with a better user experience.
          </p>
          <div className="flex items-center space-x-2">
            <Switch id="necessary-cookies" checked disabled />
            <Label htmlFor="necessary-cookies">Necessary Cookies</Label>
          </div>
          <div className="flex items-center space-x-2 mt-2">
            <Switch 
              id="statistics-cookies" 
              checked={statisticsConsent}
              onCheckedChange={setStatisticsConsent}
            />
            <Label htmlFor="statistics-cookies">Statistics</Label>
          </div>
          <div className="mt-4">
            <Link href="/privacy" className="text-sm text-primary hover:underline">
              Privacy Policy
            </Link>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end space-x-2">
          <Button variant="outline" onClick={handleDecline}>Decline</Button>
          <Button onClick={handleAccept}>Accept</Button>
        </CardFooter>
      </Card>
    </div>
  )
}

declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
  }
}
